import { useEffect, useState } from "react";
import { Download, FileText } from "lucide-react";
import { PortalShell, usePortalAccount } from "./PortalShell";
import { portalApi, portalErr, downloadFile } from "./portalApi";
import "../test/testTheme.css";

const REPORTS = [
  ["latest", "Download Latest Report", "Your most recent quiz/test with question-wise answers."],
  ["monthly", "Download Monthly Report", "This month's attempts, chapter accuracy and revision plan."],
  ["chapters", "Download Chapter-wise Report", "Full chapter-by-chapter performance breakdown."],
  ["history", "Download Test History", "Every recorded attempt with score and accuracy."],
];

function ReportInner() {
  const account = usePortalAccount();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");

  useEffect(() => {
    portalApi.get("/portal/report").then(({ data: d }) => setData(d)).catch(() => setError(""));
  }, []);

  const download = async (type) => {
    setBusy(type);
    setError("");
    try {
      await downloadFile(`/portal/report/pdf?type=${type}`, `physics-report-${type}-${account.student_id}.pdf`);
    } catch (err) {
      setError(err.response?.status === 409 ? "No quiz or test records yet — attempt a quiz first." : portalErr(err));
    } finally { setBusy(""); }
  };

  return (
    <div className="tp-wrap" style={{ maxWidth: 760, padding: "44px 22px 70px" }}>
      <div className="tp-eyebrow">MY PHYSICS REPORT</div>
      <h1 className="tp-h2" style={{ fontSize: 32 }} data-testid="report-title">Your performance, on paper</h1>
      <p className="tp-muted">Every report is generated live from your actual quiz and test records — {account.name} · {account.student_id}.</p>
      {error && <p className="tp-error" role="alert" data-testid="report-error">{error}</p>}
      {data && data.total_questions === 0 && <div className="tp-panel" style={{ textAlign: "center" }} data-testid="report-empty"><p className="tp-muted">No records yet. Take your first quiz and your report will build itself.</p></div>}
      {data && data.total_questions > 0 && (
        <div className="tp-panel" style={{ margin: "18px 0" }} data-testid="report-summary">
          <div className="tp-score-grid" style={{ margin: 0 }}>
            <div className="tp-stat"><b>{data.overall_accuracy}%</b><span>ACCURACY</span></div>
            <div className="tp-stat"><b>{data.total_quizzes}</b><span>TESTS / QUIZZES</span></div>
            <div className="tp-stat"><b>{data.total_questions}</b><span>QUESTIONS</span></div>
            <div className="tp-stat"><b>{data.correct}</b><span>CORRECT</span></div>
          </div>
        </div>
      )}
      <div className="tp-cards" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))" }} data-testid="report-cards">
        {REPORTS.map(([type, label, desc]) => (
          <div className="tp-card" key={type}>
            <div className="tp-ico"><FileText size={20} /></div>
            <h3 style={{ fontSize: 16 }}>{label}</h3>
            <p>{desc}</p>
            <button className="tp-btn secondary small" style={{ marginTop: 10 }} disabled={busy === type} onClick={() => download(type)} data-testid={`report-download-${type}`}>
              <Download size={14} /> {busy === type ? "Generating…" : "DOWNLOAD PDF"}
            </button>
          </div>
        ))}
      </div>
      <p className="tp-note" style={{ textAlign: "center", marginTop: 18 }}>Reports are private to your account. Answer-sheet uploads are not part of this platform.</p>
    </div>
  );
}

export default function PortalReport() {
  return <PortalShell><ReportInner /></PortalShell>;
}
